import { EinweisungOnlineTeilnahme, SigeEinweisung } from '../sige-plan/types';
import { zaehleTeilnahmen } from './einweisungLogic';

/**
 * Reine Helfer fuer die Innendienst-Ansichten der Online-Einweisung
 * (Ablage, Teilnahmen-Ansicht, Editor-Block). Alles ohne DOM, damit es sich
 * ohne jsdom pruefen laesst; die Komponenten bleiben duenn.
 *
 * Die fachlichen Kernfunktionen (linkIstAktiv, vereinigeTeilnehmer,
 * zaehleTeilnahmen …) liegen in einweisungLogic.ts — hier steht nur, was die
 * Oberflaeche zum Beschriften und Filtern braucht.
 */

/** Zeitstempel als TT.MM.JJJJ, HH:MM (Ortszeit). Unlesbares bleibt stehen. */
export const formatZeitpunkt = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** Zaehlstand einer Einweisung, wie ihn zaehleTeilnahmen liefert. */
export type TeilnahmenStand = ReturnType<typeof zaehleTeilnahmen>;

/**
 * Teilnehmerzahl in der Ablage: „7" oder „7 (3 online)".
 * Ohne Online-Teilnahmen bleibt die Anzeige wie bisher — die Zahl allein.
 */
export const beschreibeTeilnehmerZahl = (stand: TeilnahmenStand): string =>
  stand.online > 0 ? `${stand.gesamt} (${stand.online} online)` : String(stand.gesamt);

/** Kopfzeile der Teilnahmen-Ansicht: „3 online, 4 auf dem Gerät, 7 gesamt". */
export const beschreibeTeilnahmenKopf = (stand: TeilnahmenStand): string =>
  `${stand.online} online, ${stand.geraet} auf dem Gerät, ${stand.gesamt} gesamt`;

/**
 * Die Firmen der Online-Teilnahmen fuer den Filter — je Schreibweise einmal,
 * ohne Gross/Klein-Dubletten, alphabetisch. Der erste Treffer bestimmt die
 * angezeigte Schreibweise.
 */
export const firmenAusTeilnahmen = (liste: EinweisungOnlineTeilnahme[]): string[] => {
  const gesehen = new Map<string, string>();
  for (const t of liste ?? []) {
    const firma = (t.firma || '').trim();
    if (!firma) continue;
    const schluessel = firma.toLocaleLowerCase('de-DE');
    if (!gesehen.has(schluessel)) gesehen.set(schluessel, firma);
  }
  return [...gesehen.values()].sort((a, b) => a.localeCompare(b, 'de-DE'));
};

/** Filtert nach Firma (Gross/Klein egal); leerer Filter = alle. */
export const filtereOnlineNachFirma = (
  liste: EinweisungOnlineTeilnahme[],
  firma: string,
): EinweisungOnlineTeilnahme[] => {
  const gesucht = (firma || '').trim().toLocaleLowerCase('de-DE');
  if (!gesucht) return liste ?? [];
  return (liste ?? []).filter((t) => (t.firma || '').trim().toLocaleLowerCase('de-DE') === gesucht);
};

/**
 * Fusszeile des Aushangs: Titel · Plan-Nr. · Stand. Die Nummer selbst setzt
 * `erzeugeAushangPdf` davor („Einweisung Nr. n"), deshalb steht sie hier nicht.
 */
export const aushangFussTitel = (
  e: Pick<SigeEinweisung, 'titel' | 'plan_nr' | 'plan_stand'>,
): string | null => {
  const teile = [
    (e.titel || '').trim(),
    (e.plan_nr || '').trim() ? `Plan-Nr. ${e.plan_nr!.trim()}` : '',
    (e.plan_stand || '').trim() ? `Stand ${e.plan_stand!.trim()}` : '',
  ].filter(Boolean);
  return teile.length > 0 ? teile.join(' · ') : null;
};

/**
 * Wann der Knopf „Teilnahmen" in der Ablage steht: bei jedem abgeschlossenen
 * Blatt (dort kann der Link aktiv sein) — und bei einem Entwurf nur, wenn
 * wider Erwarten schon Online-Zeilen vorliegen, damit sie nie unsichtbar sind.
 */
export const zeigeTeilnahmenKnopf = (
  e: Pick<SigeEinweisung, 'status'>,
  stand: Pick<TeilnahmenStand, 'online'>,
): boolean => e.status === 'abgeschlossen' || stand.online > 0;

/** Tooltip des Link-Knopfes im Entwurf. */
export const LINK_ENTWURF_HINWEIS = 'Erst abschließen, dann Link aktivieren';
